// De weergave van de SiSa/SPUK-indicatoren: één lijst rijen voor de tabel én
// het Excel-bestand, zodat die twee nooit uit elkaar lopen. Plus de kanttekeningen die
// zeggen wat er niet meetelt of een benadering is (T24 van
// docs/features/huishouden-vaste-gegevens.md).
//
// Een onbekend bedrag (tarief niet ingesteld) staat er als tekst, niet als €0:
// dit is het getal dat in een verantwoording wordt overgeschreven.
// AI-CONTEXT: See docs/pitfalls.md#ui-aggregate-collapses-nothing-into-zero

import { SISA_MEASURE_CATEGORIES } from '../materialsHelpers/sisaMeasureCategory'
import { readRegistrationDate } from '../materialsHelpers/registrationDate'

/**
 * @typedef {object} SisaRow
 * @property {string} id
 * @property {string} nr - Nummer van de indicator zoals in de SiSa-bijlage ('1' … '8.6')
 * @property {string} group - Onderdeel: 'Eigendom', 'Besteding voorzieningen', 'Advies aan huis' of 'Maatregelen'
 * @property {string} label
 * @property {string} value - Voor de lezer: '2', '€ 96,00' of 'Tarief niet ingesteld'
 * @property {number|null} raw - Het getal zelf; null = onbekend
 */

const euro = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' })
const getal = new Intl.NumberFormat('nl-NL')

/** @param {number|null} value @returns {string} */
const formatEuro = (value) => (value === null ? 'Tarief niet ingesteld' : euro.format(value))

/**
 * @param {import('./computeSisaIndicators').SisaIndicators} ind
 * @returns {Array<SisaRow>}
 */
const buildSisaRows = (ind) => {
    const T = ind.year
    // Onderdeel per indicator. 'Eigendom' is een besluit van 2026-10-28: het
    // vaste gegeven 'Type woning' staat in de verantwoording onder die kop.
    /** @param {string} nr @returns {string} */
    const group = (nr) => {
        if (nr === '1' || nr === '2') return 'Eigendom'
        if (nr === '3' || nr === '4') return 'Besteding voorzieningen'
        if (nr.startsWith('8')) return 'Maatregelen'
        return 'Advies aan huis'
    }
    /** @param {string} nr @param {string} label @param {number} n @returns {SisaRow} */
    const aantal = (nr, label, n) => ({ id: `sisa-${nr}`, nr, group: group(nr), label, value: getal.format(n), raw: n })
    /** @param {string} nr @param {string} label @param {number|null} n @returns {SisaRow} */
    const bedrag = (nr, label, n) => ({ id: `sisa-${nr}`, nr, group: group(nr), label, value: formatEuro(n), raw: n })

    return [
        aantal('1', `Huishoudens in een huurwoning met voorzieningen in ${T}`, ind.huur.count),
        aantal('2', `Huishoudens in een koopwoning met voorzieningen in ${T}`, ind.koop.count),
        bedrag('3', `Besteding in ${T} voor 1 en 2 (materiaal en uren uitvoering)`, ind.besteding.totaal),
        bedrag('4', `Besteding voor 1 en 2 tot en met ${T}`, ind.bestedingCumulatief.totaal),
        aantal('5', `Huishoudens met advies aan huis in ${T}`, ind.advies.count),
        bedrag('6', `Besteding advies in ${T} (uren advies)`, ind.adviesBesteding),
        bedrag('7', `Besteding advies tot en met ${T}`, ind.adviesBestedingCumulatief),
        ...SISA_MEASURE_CATEGORIES.map((c, i) => aantal(`8.${i + 1}`, `${c.label} in ${T}`, ind.maatregelen[c.key] || 0)),
    ]
}

/**
 * @param {import('./computeSisaIndicators').SisaIndicators} ind
 * @returns {Array<string>} Kanttekeningen voor onder de tabel; leeg als alles klopt
 */
const buildSisaNotes = (ind) => {
    const notes = []
    const onbekend = ind.eigendomOnbekend.count
    if (onbekend > 0) {
        notes.push(onbekend === 1
            ? `1 huishouden met voorzieningen in ${ind.year} telt niet mee bij 1 tot en met 4: 'Type woning' is niet ingevuld.`
            : `${onbekend} huishoudens met voorzieningen in ${ind.year} tellen niet mee bij 1 tot en met 4: 'Type woning' is niet ingevuld.`)
    }
    // De cumulatieve telling omvat die van het jaar.
    const huidig = ind.prijsIsHuidigCumulatief
    if (huidig > 0) {
        notes.push(huidig === 1
            ? '1 materiaalregel rekent met de huidige prijs: bij het registreren is geen prijs vastgelegd.'
            : `${huidig} materiaalregels rekenen met de huidige prijs: bij het registreren is geen prijs vastgelegd.`)
    }
    const { uitvoering, advies } = ind.tariefOntbreekt
    const waar = "(Instellingen → Ondersteuning → 'Uurtarieven')"
    if (uitvoering && advies) notes.push(`Uurtarief uitvoering en uurtarief advies zijn niet ingesteld ${waar}.`)
    else if (uitvoering) notes.push(`Uurtarief uitvoering is niet ingesteld ${waar}.`)
    else if (advies) notes.push(`Uurtarief advies is niet ingesteld ${waar}.`)
    return notes
}

/**
 * Het verantwoordingsjaar bij een periode: het jaar van het einde, anders van
 * het begin, anders `fallbackYear` (een template: het laatste jaar van zijn
 * voorbeeldgegevens), anders het lopende jaar.
 * @param {{start?: string|null, end?: string|null}|null} range
 * @param {Date} [now]
 * @param {number|null} [fallbackYear]
 * @returns {number}
 */
const sisaYearFor = (range, now = new Date(), fallbackYear = null) => {
    const iso = range?.end || range?.start
    if (iso) return Number(String(iso).slice(0, 4))
    return fallbackYear ?? now.getFullYear()
}

/**
 * Het laatste jaar waarin een materiaalregel geregistreerd is.
 * @param {Array<{RegisteredAt?: *, Timestamp?: *}>} entries
 * @returns {number|null}
 */
const latestRegistrationYear = (entries) => {
    const years = (entries || [])
        .map((e) => readRegistrationDate(e).ms)
        .filter((ms) => ms !== null)
        .map((ms) => new Date(ms).getFullYear())
    return years.length ? Math.max(...years) : null
}

/**
 * De kerncijfers die de weergave 'Kerncijfer' van de widget kan tonen. Een som
 * van indicatoren (huishoudens, maatregelen, besteding) staat hier en nergens
 * anders, zodat de rapportage en het Excel-bestand dezelfde getallen geven.
 * @type {Array<{key: string, label: string}>}
 */
const SISA_KEY_FIGURES = [
    { key: 'jaar', label: 'Verantwoordingsjaar' },
    { key: 'huishoudens', label: 'Huishoudens met voorzieningen' },
    { key: 'huur', label: 'Huishoudens in een huurwoning' },
    { key: 'koop', label: 'Huishoudens in een koopwoning' },
    { key: 'advies', label: 'Huishoudens met advies aan huis' },
    { key: 'maatregelen', label: 'Maatregelen' },
    { key: 'besteding', label: 'Besteding' },
    { key: 'bestedingCumulatief', label: 'Besteding totaal' },
    { key: 'bestedingVoorzieningen', label: 'Besteding voorzieningen' },
    { key: 'bestedingVoorzieningenCumulatief', label: 'Besteding voorzieningen totaal' },
    { key: 'bestedingAdvies', label: 'Besteding advies' },
    { key: 'bestedingAdviesCumulatief', label: 'Besteding advies totaal' },
    ...SISA_MEASURE_CATEGORIES.map((c) => ({ key: `maatregel:${c.key}`, label: c.label })),
]

/** @param {Array<number|null>} parts @returns {number|null} Onbekend zodra één deel onbekend is */
const sumKnown = (parts) => (parts.some((p) => p === null) ? null : parts.reduce((a, b) => a + b, 0))

/**
 * Eén kerncijfer, opgemaakt.
 * @param {import('./computeSisaIndicators').SisaIndicators} ind
 * @param {string} key - Uit SISA_KEY_FIGURES
 * @returns {{label: string, value: string, raw: number|null}}
 */
const sisaKeyFigure = (ind, key) => {
    const T = ind.year
    const aantal = (label, n) => ({ label, value: getal.format(n), raw: n })
    const bedrag = (label, n) => ({ label, value: formatEuro(n), raw: n })
    if (key.startsWith('maatregel:')) {
        const cat = SISA_MEASURE_CATEGORIES.find((c) => `maatregel:${c.key}` === key)
        if (cat) return aantal(`${cat.label} in ${T}`, ind.maatregelen[cat.key] || 0)
    }
    switch (key) {
        case 'jaar': return { label: 'Verantwoordingsjaar', value: String(T), raw: T }
        case 'huishoudens': return aantal(`Huishoudens met voorzieningen in ${T}`, ind.huur.count + ind.koop.count)
        case 'huur': return aantal(`In een huurwoning`, ind.huur.count)
        case 'koop': return aantal(`In een koopwoning`, ind.koop.count)
        case 'advies': return aantal(`Huishoudens met advies aan huis in ${T}`, ind.advies.count)
        case 'maatregelen': return aantal(`Maatregelen in ${T}`, Object.values(ind.maatregelen).reduce((a, b) => a + b, 0))
        case 'besteding': return bedrag(`Besteed in ${T}`, sumKnown([ind.besteding.totaal, ind.adviesBesteding]))
        case 'bestedingCumulatief': return bedrag(`Besteed tot en met ${T}`, sumKnown([ind.bestedingCumulatief.totaal, ind.adviesBestedingCumulatief]))
        case 'bestedingVoorzieningen': return bedrag(`Voorzieningen in ${T}`, ind.besteding.totaal)
        case 'bestedingVoorzieningenCumulatief': return bedrag(`Voorzieningen tot en met ${T}`, ind.bestedingCumulatief.totaal)
        case 'bestedingAdvies': return bedrag(`Advies in ${T}`, ind.adviesBesteding)
        case 'bestedingAdviesCumulatief': return bedrag(`Advies tot en met ${T}`, ind.adviesBestedingCumulatief)
        default: throw new Error(`sisaKeyFigure: onbekend kerncijfer '${key}'`)
    }
}

export { buildSisaRows, buildSisaNotes, sisaYearFor, latestRegistrationYear, sisaKeyFigure, SISA_KEY_FIGURES }
export default buildSisaRows
